<template>
    <b-modal :visible="value" @change="onVisibleChange" title="Void Payment" hide-footer>
        <b-alert :show="reasonCodesUnavailable" variant="warning" class="py-2">
            A void requires a reason code, but no Statement Reason / Credit Codes are configured.
            An administrator must add them under Configuration &gt; AR / Patient Billing.
        </b-alert>

        <p v-if="payment">
            Void this payment of <strong>{{ currency(payment.amount) }}</strong>{{ paymentDetail }}?
        </p>

        <!-- A card CPHub charged is refunded as part of the void, so say so before it happens. A
             card processed outside the system carries only a reference and must be refunded there. -->
        <b-alert :show="wasChargedThroughStripe" variant="info" class="py-2">
            This payment was charged to the patient's card, so voiding it also refunds
            {{ currency(payment && payment.amount) }} to that card.
        </b-alert>
        <b-form-group :label="reasonCodeLabel">
            <b-select v-if="useReasonCodeList" v-model="reason" :options="reasonCodeOptions"
                :disabled="reasonCodesUnavailable" />
            <b-form-textarea v-else v-model="reason" rows="2" placeholder="Reason for voiding this payment" />
        </b-form-group>
        <small class="text-muted">
            The balance is restored and any charges this payment covered become open again.
        </small>

        <div class="text-right mt-2">
            <b-button variant="secondary" class="mr-2" :disabled="saving" @click="close">Cancel</b-button>
            <b-button variant="danger" :disabled="saving || reasonCodesUnavailable || !reason" @click="voidPayment">
                Void Payment
            </b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { ARPaymentView, arPaymentMethodLabel } from "@/models/AR";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import ARReasonCodesMixin from "@/mixins/ARReasonCodesMixin";

@Component({
    name: "ARVoidPaymentModal",
})
export default class ARVoidPaymentModal extends Mixins(ARReasonCodesMixin) {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    // The payment being voided is picked on the ledger row it belongs to, so the modal is told
    // which payment to act on rather than presenting a list to choose from.
    @Prop({ default: null }) payment!: ARPaymentView | null;

    private saving = false;
    private reason = "";

    // Only a card CPHub itself charged carries a Stripe charge id, so this — not the payment method —
    // is what distinguishes a void that refunds from one that only reverses the ledger.
    get wasChargedThroughStripe(): boolean {
        return !!this.payment?.stripeChargeId;
    }

    // Method and post date identify which payment is about to be voided. Either may be missing on
    // an older record, so only the parts that are present are shown.
    get paymentDetail(): string {
        const parts = [
            arPaymentMethodLabel(this.payment?.paymentMethod),
            this.formatDate(this.payment?.created),
        ].filter(Boolean);
        return parts.length ? ` (${parts.join(", ")})` : "";
    }

    @Watch("value")
    async onShow(visible: boolean): Promise<void> {
        if (!visible) return;
        this.reason = "";
        await this.loadReasonCodeConfiguration();
        this.reason = this.knownReasonCode(this.reason);
    }

    async voidPayment(): Promise<void> {
        if (!this.payment || !this.reason) return;
        try {
            this.saving = true;
            await axios.post(`/AR/payments/${this.payment.id}/void`, { reasonCode: this.reason });
            this.$notification(NotificationOptions.success("Payment voided."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }

    formatDate(date?: string): string {
        return date ? moment.utc(date).local().format("L") : "";
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
